import { AxiosError } from "axios";
import { api } from "../axios";

export type Devise = "USD" | "CDF";
export type Frequence = "PONCTUEL" | "HEBDOMADAIRE" | "MENSUEL" | "ANNUEL";
export type PlanType = "MENAGE" | "ENTREPRISE" | "PROFESSIONNEL" | "CHANTIER";

export interface Zone {
  id: number;
  nom: string;
}

export interface PlanAbonnement {
  id: number;
  nom: string;
  montant: number;
  prixBase?: number;
  devise: Devise;
  type?: PlanType;
  frequence: Frequence;
  passagesParMois?: number;
  collecteIncluse?: boolean;
  description?: string | null;
  zoneId?: number | null;
  zone?: Zone | null;
  dotations?: Array<{
    id?: number;
    type?: "POUBELLE" | "BAC" | "SACS" | null;
    resourceCatalogId: number;
    resourceCatalog?: {
      id: number;
      code: string;
      nom: string;
      famille?: string | null;
      actif?: boolean;
      stockDisponible?: number;
    } | null;
    quantite: number;
    isRequired?: boolean;
    regleFourniture:
      | "UNIQUE_DEMARRAGE"
      | "RECURRENT_MENSUEL"
      | "SUR_DEMANDE"
      | "REMPLACEMENT_CASSE"
      | "PAR_PASSAGE";
  }>;
  services?: Array<{
    id?: number;
    type: "NETTOYAGE" | "DESINFECTION" | "LAVAGE_BAC";
    actif: boolean;
    prixSupplementaire?: number | null;
  }>;
  actif?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePlanPayload {
  nom: string;
  montant: number;
  devise: Devise;
  type?: PlanType;
  frequence: Frequence;
  passagesParMois?: number;
  zoneId?: number | null;
  description?: string | null;
  dotations?: PlanAbonnement["dotations"];
  services?: PlanAbonnement["services"];
}

export type UpdatePlanPayload = Partial<CreatePlanPayload>;

const extractErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "response" in error) {
    const axiosErr = error as AxiosError<{ error?: string; message?: string }>;
    return (
      axiosErr.response?.data?.message ||
      axiosErr.response?.data?.error ||
      axiosErr.message ||
      "Erreur API"
    );
  }
  return error instanceof Error ? error.message : "Erreur inattendue";
};

function normalizePlan(raw: Record<string, unknown>): PlanAbonnement | null {
  const id = Number(raw.id);
  if (!Number.isFinite(id)) return null;
  const zoneRaw = raw.zone as Record<string, unknown> | null | undefined;

  const prixBaseRaw = raw.prixBase;
  const montantRaw = raw.montant;
  const prixBase = Number(String(prixBaseRaw ?? "").replace(",", "."));
  const montant = Number(String(montantRaw ?? "").replace(",", "."));
  const montantFinal = Number.isFinite(prixBase)
    ? prixBase
    : Number.isFinite(montant)
      ? montant
      : 0;

  return {
    id,
    nom: String(raw.nom ?? ""),
    montant: montantFinal,
    prixBase: montantFinal,
    devise: (raw.devise === "CDF" ? "CDF" : "USD") as Devise,
    type: (
      ["MENAGE", "ENTREPRISE", "PROFESSIONNEL", "CHANTIER"].includes(String(raw.type))
        ? raw.type
        : "MENAGE"
    ) as PlanType,
    frequence: (
      ["PONCTUEL", "HEBDOMADAIRE", "MENSUEL", "ANNUEL"].includes(String(raw.frequence))
        ? raw.frequence
        : "PONCTUEL"
    ) as Frequence,
    description:
      raw.description === null || raw.description === undefined ? null : String(raw.description),
    passagesParMois: raw.passagesParMois != null ? Number(raw.passagesParMois) : undefined,
    collecteIncluse:
      typeof raw.collecteIncluse === "boolean" ? raw.collecteIncluse : true,
    zoneId:
      raw.zoneId != null
        ? Number(raw.zoneId)
        : zoneRaw?.id != null
          ? Number(zoneRaw.id)
          : null,
    zone:
      zoneRaw && zoneRaw.id != null && zoneRaw.nom != null
        ? { id: Number(zoneRaw.id), nom: String(zoneRaw.nom) }
        : null,
    dotations: Array.isArray(raw.dotations)
      ? raw.dotations
          .filter((d): d is Record<string, unknown> => !!d && typeof d === "object")
          .map((d) => ({
            id: d.id != null ? Number(d.id) : undefined,
            type: d.type == null ? null : (String(d.type) as "POUBELLE" | "BAC" | "SACS"),
            resourceCatalogId: Number(d.resourceCatalogId ?? 0),
            resourceCatalog:
              d.resourceCatalog && typeof d.resourceCatalog === "object"
                ? {
                    id: Number((d.resourceCatalog as Record<string, unknown>).id),
                    code: String((d.resourceCatalog as Record<string, unknown>).code ?? ""),
                    nom: String((d.resourceCatalog as Record<string, unknown>).nom ?? ""),
                    famille:
                      (d.resourceCatalog as Record<string, unknown>).famille == null
                        ? null
                        : String((d.resourceCatalog as Record<string, unknown>).famille),
                    actif: (d.resourceCatalog as Record<string, unknown>).actif !== false,
                    stockDisponible: Number((d.resourceCatalog as Record<string, unknown>).stockDisponible ?? 0),
                  }
                : null,
            quantite: Math.max(1, Number(d.quantite ?? 1)),
            isRequired: d.isRequired !== false,
            regleFourniture: String(
              d.regleFourniture ?? "SUR_DEMANDE"
            ) as "UNIQUE_DEMARRAGE" | "RECURRENT_MENSUEL" | "SUR_DEMANDE" | "REMPLACEMENT_CASSE",
          }))
      : [],
    services: Array.isArray(raw.services)
      ? raw.services
          .filter((s): s is Record<string, unknown> => !!s && typeof s === "object")
          .map((s) => ({
            id: s.id != null ? Number(s.id) : undefined,
            type: String(s.type ?? "NETTOYAGE") as "NETTOYAGE" | "DESINFECTION" | "LAVAGE_BAC",
            actif: s.actif !== false,
            prixSupplementaire:
              s.prixSupplementaire == null ? null : Number(s.prixSupplementaire),
          }))
      : [],
    actif: typeof raw.actif === "boolean" ? raw.actif : undefined,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : undefined,
    updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : undefined,
  };
}

export const getPlansAbonnement = async (): Promise<PlanAbonnement[]> => {
  try {
    const { data } = await api.get<unknown>("/plans", { params: { page: 1, limit: 200 } });
    const payload = data as { data?: unknown[] } | unknown[];
    const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
    const out: PlanAbonnement[] = [];
    for (const item of rows) {
      if (!item || typeof item !== "object") continue;
      const n = normalizePlan(item as Record<string, unknown>);
      if (n) out.push(n);
    }
    return out;
  } catch {
    return [];
  }
};

export const getPlansByZone = async (zoneId: number): Promise<PlanAbonnement[]> => {
  const all = await getPlansAbonnement();
  return all.filter((p) => p.zoneId == null || p.zoneId === zoneId);
};

export const getPlanById = async (id: number): Promise<PlanAbonnement | null> => {
  try {
    const { data } = await api.get<Record<string, unknown>>(`/plans/${id}`);
    return normalizePlan(data);
  } catch {
    return null;
  }
};

export const createPlanAbonnement = async (payload: CreatePlanPayload): Promise<PlanAbonnement> => {
  try {
    const created = await api.post<Record<string, unknown>>("/plans", {
      nom: payload.nom,
      prixBase: payload.montant,
      devise: payload.devise,
      type: payload.type ?? "MENAGE",
      frequence: payload.frequence,
      passagesParMois: payload.passagesParMois ?? 4,
      zoneId: payload.zoneId ?? null,
      description: payload.description?.trim() ? payload.description.trim() : null,
      actif: true,
      dotations: payload.dotations ?? [],
      services: payload.services ?? [],
    });
    const id = Number(created.data.id);
    const detail = await getPlanById(id);
    if (!detail) throw new Error("Réponse invalide");
    return detail;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const updatePlanAbonnement = async (
  id: number,
  payload: UpdatePlanPayload
): Promise<PlanAbonnement> => {
  try {
    const body: Record<string, unknown> = {};
    if (payload.nom !== undefined) body.nom = payload.nom;
    if (payload.montant !== undefined) body.prixBase = payload.montant;
    if (payload.devise !== undefined) body.devise = payload.devise;
    if (payload.type !== undefined) body.type = payload.type;
    if (payload.frequence !== undefined) body.frequence = payload.frequence;
    if (payload.passagesParMois !== undefined) body.passagesParMois = payload.passagesParMois;
    if (payload.zoneId !== undefined) body.zoneId = payload.zoneId;
    if (payload.description !== undefined) {
      body.description = payload.description?.trim() ? payload.description.trim() : null;
    }
    if (payload.dotations !== undefined) body.dotations = payload.dotations;
    if (payload.services !== undefined) body.services = payload.services;
    await api.put(`/plans/${id}`, body);
    const detail = await getPlanById(id);
    if (!detail) throw new Error("Réponse invalide");
    return detail;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const deletePlanAbonnement = async (id: number): Promise<void> => {
  try {
    await api.delete(`/plans/${id}`);
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

